/**
 * PageHero — shared navy-spotlight hero header used across calculators and
 * content pages so every corner of the app feels like the Dashboard.
 *
 * Renders:
 *   • pill badge with optional icon
 *   • large amber-accented H1
 *   • single-line tagline
 *   • optional left "Back" button + right actions slot (e.g. ThemeToggle)
 *   • subtle amber radial glows in the background
 *
 * Usage:
 *   <PageHero
 *     eyebrow="Business planning"
 *     eyebrowIcon={Calculator}
 *     title={<>Business <span className="text-amber-brand">Calculator</span></>}
 *     description="Model costs, margins and break-even for a real freeze-dried business."
 *     backTo="/dashboard"
 *     backLabel="Dashboard"
 *     actions={<ThemeToggle />}
 *   />
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const PageHero = ({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  description,
  backTo,
  backLabel = 'Back',
  actions,
  testId = 'page-hero',
  children,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-navy-spotlight text-background relative overflow-hidden" data-testid={testId}>
      <div className="container mx-auto px-4 py-8 sm:py-10 relative z-10">
        {(backTo || actions) && (
          <div className="flex items-center gap-3 mb-4">
            {backTo && (
              <Button
                variant="ghost"
                onClick={() => navigate(backTo)}
                className="text-background/80 hover:text-background hover:bg-background/10 rounded-full h-9 px-3"
                data-testid="page-hero-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                {backLabel}
              </Button>
            )}
            {actions && <div className="ml-auto">{actions}</div>}
          </div>
        )}
        <div className="max-w-3xl">
          {eyebrow && (
            <span className="pill-badge">
              {EyebrowIcon && <EyebrowIcon className="w-3.5 h-3.5" />}
              {eyebrow}
            </span>
          )}
          {title && (
            <h1 className={`font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight ${eyebrow ? 'mt-4' : ''} text-balance`}>
              {title}
            </h1>
          )}
          {description && (
            <p className="mt-3 text-base sm:text-lg text-background/70 max-w-xl">
              {description}
            </p>
          )}
          {children && <div className="mt-5">{children}</div>}
        </div>
      </div>
      {/* Soft amber glows */}
      <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
    </div>
  );
};

export default PageHero;
